import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {InvoiceInputDialogResult} from '@invoice-upload/models/invoice-input-dialog-result';
import {InvoiceUpload} from '@invoice-upload/models/invoice-upload';
import {InvoiceInputDialogParameters} from '@invoice-upload/models/invoice-input-dialog-parameters';
import {catchError, combineLatest, empty, finalize, map, Observable, of, Subject, take, takeUntil, tap} from 'rxjs';
import {InvoiceDocumentFormService} from '@feature/invoice-upload/services/invoice-document-form.service';
import {UntypedFormGroup, Validators} from '@angular/forms';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {VatRate} from '@invoice-upload/models/vat-rate';
import {MojoFormsInvoiceService} from '@feature/invoice-upload/services/mojo-forms-invoice.service';
import {ToastrService} from 'ngx-toastr';
import {Creditor} from '@feature/creditor-accounts/models/creditor-account-model';
import {Dialog} from 'primeng/dialog';
import {InvoiceDocumentService} from "@feature/invoice-upload";
import {GroupedNameValuePairs} from "@common/models/name-value";

@Component({
  selector: 'mojo-invoice-input-dialog',
  templateUrl: './invoice-input-dialog.component.html'
})
export class InvoiceInputDialogComponent implements OnInit {

  style = {width: '980px', height: '800px'};

  form: UntypedFormGroup;

  @ViewChild('additionalDocs') additionalDocs;

  @ViewChild('dialog') dialog;

  @ViewChild('docDate') documentDate;

  displayDialog: boolean;

  maxNumeric = 99999999999.99;

  @Input()
  accounts: Creditor[];

  @Input()
  showAccounts: boolean;

  @Input()
  documentDetailsExpanded = true;

  @Input()
  billingAmountExpanded = true;

  @Input()
  billingDetailsExpanded = true;

  @Input()
  additionalDocumentsExpanded = true;

  @Input()
  formDesignId: number;

  @Input()
  defaultDate = new Date();

  @Input()
  worksAddonEnabled: boolean = false;

  @Input()
  product: string;

  billingAmountHeader: Observable<string>;
  purchaseOrdersAndContracts: Observable<GroupedNameValuePairs[]>

  private creditNoteTransactionTypeCode: string;
  private additionalDocumentFiles: File[] = [];
  private invoicePdf: Blob;
  private invoiceUpload: InvoiceUpload;
  private uploadInProgress = false;
  isContractSelected: Observable<boolean>;

  @Input()
  availableDocumentTypes: SelectItem[];

  @Input()
  vatRates: VatRate[];


  @Output()
  invoiceFormUploaded = new EventEmitter<InvoiceUpload>();

  @Output()
  closeDialog: EventEmitter<InvoiceInputDialogResult> = new EventEmitter<InvoiceInputDialogResult>();

  private unsubscribe = new Subject<void>();

  pdfSource: string | Blob = '';

  constructor(
    private readonly messageService: ToastrService,
    private readonly mojoFormsInvoiceService: MojoFormsInvoiceService,
    private readonly confirmationService: ConfirmationService,
    private readonly cd: ChangeDetectorRef,
    private readonly formService: InvoiceDocumentFormService,
    private readonly invoiceDocumentService: InvoiceDocumentService) {
  }

  calculateVatFromNetAmount() {
    const net = this.form.get('netAmount').value;
    const v = this.form.get('vatRate').value as VatRate;
    if (net == undefined || v == undefined) {
      return;
    }
    const vatAmount = Number(((net * v.VatPercent) / 100).toFixed(2));
    this.form.get('vatAmount').setValue(vatAmount, {onlySelf: true, emitEvent: true});
  }

  calculateGrossFromVatAmount() {
    const vatAmount = this.form.get('vatAmount').value;
    const netAmount = this.form.get('netAmount').value;
    const gross = vatAmount + netAmount;
    this.form.get('grossAmount').setValue(gross, {onlySelf: true, emitEvent: false});
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getAvailableDocumentType() {
    return this.availableDocumentTypes.map(a => {
      return {label: a, value: a};
    });
  }

  showInvoiceInputDialog(args: InvoiceInputDialogParameters) {

    if (args) {
      this.creditNoteTransactionTypeCode = args.creditNoteTransactionTypeCode;
      this.invoiceUpload = args.invoiceUpload;
      this.invoicePdf = args.invoicePdf;
      try {
        this.pdfSource = args.invoicePdf;
      } catch (error) {
        console.error('Error initializing PDF viewer:', error);
      }
    }
    this.displayDialog = true;
    this.cd.detectChanges();
    this.reset();
  }


  reset() {
    this.form.reset();
    this.additionalDocs.reset();
    this.additionalDocumentFiles.length = 0;
  }

  send() {
    const model = this.formService.formToModel(this.form, () => this.accounts);
    if (model.DocumentTypeCode.toLowerCase() === this.creditNoteTransactionTypeCode.toLowerCase()) {
      model.NetAmount = -Math.abs(model.NetAmount);
      model.VatAmount = -Math.abs(model.VatAmount);
      model.GrossAmount = -Math.abs(model.GrossAmount)
    }

    let invoiceFile: File;
    try {
      invoiceFile = new File([this.invoicePdf], this.invoiceUpload.FileName);
    } catch (e) {
      // try to create manually if ie/safari
      console.warn('Cannot call file constructor using blob');
      let copy: any = this.invoicePdf.slice();
      copy.name = this.invoiceUpload.FileName;
      copy.lastModified = new Date();
      invoiceFile = copy as File;
    }


    model.FormDesignId = this.formDesignId;

    this.uploadInProgress = true;

    const upload = this.mojoFormsInvoiceService.uploadInvoice(model,
      invoiceFile,
      this.additionalDocumentFiles,
      this.product).pipe(
      take(1),
      tap(() => {
        this.messageService.success('Invoice Sent');
        this.reset();
        this.displayDialog = false;
        this.invoiceFormUploaded.emit(this.invoiceUpload);
      }),
      catchError((e) => {
        let errorMsg = e.error ?? 'Could not send invoice';
        if (e.status && e.status == 403) {
          errorMsg = `Could not send invoice -
            You are not currently authorised to create the type of
            form that would be generated by this upload widget.`;
        }
        this.messageService.error(errorMsg, 'Upload Failure', {disableTimeOut: true});
        return empty();
      }),
      finalize(() => {
        this.uploadInProgress = false;
        this.cd.detectChanges();
      }));

    upload.subscribe();

  }

  onClose() {

    if (this.form.touched && this.form.dirty || this.additionalDocs.hasFiles()) {

      this.confirmationService.confirm({
        header: 'Invoice Upload',
        message: 'Any information entered will not be saved, would you like to continue?',
        accept: () => {
          const result = {} as InvoiceInputDialogResult;
          this.closeDialog.emit(result);
        },
        reject: () => {
          this.displayDialog = true;
          this.cd.detectChanges();
        }
      });
    }

  }

  canSend() {
    return !this.form.pristine &&
      !this.form.pending &&
      !this.form.invalid &&
      !this.uploadInProgress;
  }


  ngOnChanges(changes: SimpleChanges): void {

    if (changes['showAccounts'] && this.form) {

      this.form.get('accountFor').clearValidators();
      if (changes['showAccounts'].currentValue == true) {
        this.form.get('accountFor').setValidators([Validators.required]);
      }
    }
  }

  ngOnInit() {
    this.form = this.formService.getForm();

    const accountForChanges = this.form.get('accountFor').valueChanges;
    accountForChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.accounts.length == 1 ? this.setPurchaseOrdersAndContracts(this.accounts[0].Uri) : this.setPurchaseOrdersAndContracts(this.form.get('accountFor').value?.Uri);
        this.setBillingAccountHeader()
      })
    ).subscribe();

    const poOrContractSelectionChanges = this.form.get('purchaseOrderOrContractId').valueChanges;
    poOrContractSelectionChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        const selectedItem = this.form.get('purchaseOrderOrContractId').value?.Value;

        if(this.purchaseOrdersAndContracts != undefined) {
          this.isContractSelected = this.purchaseOrdersAndContracts.pipe(map(x => {
            if (selectedItem) {
              const isContract = x.find(group => group.Items.some(item => item.Value === selectedItem))?.Name === "Contracts";
              this.updateFromAndToDateValidators(isContract)

              return isContract;
            }
            return false;

          }))
        }
      })
    ).subscribe();


    this.setBillingAccountHeader();

    const vatAmountChanges = this.form.get('vatAmount').valueChanges;
    vatAmountChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => this.calculateGrossFromVatAmount())
    ).subscribe();

    const netAmountChanges = this.form.get('netAmount').valueChanges;
    const vatRateChanges = this.form.get('vatRate').valueChanges;
    combineLatest(netAmountChanges, vatRateChanges)
      .pipe(
        takeUntil(this.unsubscribe),
        tap(() => this.calculateVatFromNetAmount())
      ).subscribe();

    if (this.invoicePdf) {
      try {
        this.pdfSource = this.invoicePdf;
      } catch (error) {
        console.error('Error initializing PDF viewer:', error);
      }
    }
  }

  ngAfterViewInit() {
    if (this.invoicePdf) {
      try {
        this.pdfSource = this.invoicePdf;
      } catch (error) {
        console.error('Error initializing PDF viewer:', error);
      }
    }

    // Set document date field to todays date.
    // Note: This is achieved by calling private functions which are not offically exposed
    //       by primeng calendar, so this could potentially break in future primeng versions.
    (<Dialog>this.dialog).onShow.subscribe(() => {
      const today = new Date();
      this.documentDate.setDisabledState(false);
      this.documentDate.onDateSelect(
        new MouseEvent('dummy'), {
          year: today.getFullYear(),
          month: today.getMonth(),
          day: today.getDate(),
          selectable: true
        });
    });
  }

  private setBillingAccountHeader() {
    const account = this.form.get('accountFor').value;
    this.billingAmountHeader = account ? of(`Billing Amount (${account.DefaultCurrencyCode})`) : of('Billing Amount');
  }

  onFileUpload(files: File[]) {
    this.additionalDocumentFiles.push(...files);
  }

  onDeleteUpload(file: File) {
    const index = this.additionalDocumentFiles.indexOf(file, 0);
    if (index > -1) {
      this.additionalDocumentFiles.splice(index, 1);
    }
  }

  setPurchaseOrdersAndContracts(accountUri: number) {
    this.form.get('purchaseOrderOrContractId').reset()
    this.purchaseOrdersAndContracts = this.invoiceDocumentService.getPurchaseOrdersAndContractsForSupplierCreditor(accountUri)
  }

  updateFromAndToDateValidators(isContractSelected: boolean){
    this.form.get('fromDate').clearValidators();
    this.form.get('toDate').clearValidators();

    if(isContractSelected){
      this.form.get('fromDate').setValidators([Validators.required]);
      this.form.get('toDate').setValidators([Validators.required]);
    }

    this.form.get('fromDate').updateValueAndValidity();
    this.form.get('toDate').updateValueAndValidity();
  }

}
